import React, { useState, useEffect, useRef } from 'react';
import { Box, Wrapper, Row, Column, Button, Link, Copy, Optional } from '@honda-canada/design-system-react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { compiler } from 'markdown-to-jsx';
import css from '@styled-system/css';
import styled from 'styled-components';
import { stripMarkdownHeading } from '../../utils/markdown';
import { useAppName } from '../../utils/sitecoreContext';
import { HONDA_SITE_NAME } from '../../utils/constants';
import { getBapUrlParams } from './constants';
import { useOffersQueryParam } from './utils';
import Toggle, { Label } from './Toggle';
import { gtmSelectYear } from '../../utils/gtmEvents';
const linkHeight = 33;
const Options = styled(Box)(({ isOpen, theme, numOptions }) => css({
    height: isOpen ? `${numOptions * linkHeight + 18}px` : 0,
    transition: `height ${theme.transitionTimingFunction.default} ${theme.transitionDuration.t6}`,
}));
const OptionLink = styled(Link)(({ selected, color, isOpen }) => css({
    fontWeight: 'normal',
    fontFamily: 'bold',
    display: 'block',
    fontSize: '14px',
    lineHeight: '18px',
    textDecoration: 'none',
    color: selected ? 'primary' : color,
    visibility: isOpen ? 'visible' : 'hidden',
    '&:hover': {
        color: 'primary',
    },
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
}));
const ModelBadge = styled.img(css({
    ml: 'default',
}));
const TrimBadge = styled.img `
  max-height: 11px;
  position: relative;
  top: -2px;
`;
const NavItem = styled.li(css({
    height: '100%',
    display: 'flex',
    alignItems: 'center',
}));
const textStyles = {
    letterSpacing: '0.5px',
    fontWeight: 'regular',
    fontFamily: 'bold',
    fontSize: '14px',
    textDecoration: 'none',
};
const ModelBrandNameStyles = () => css({
    ...textStyles,
    ml: 'l',
    lineHeight: ['18px', '22px'],
});
const LinkStyles = ({ theme, color }) => css({
    ...textStyles,
    color,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    borderBottom: '3px solid transparent',
    boxSizing: 'border-box',
    '&:hover, &:focus': {
        color: 'primary',
        borderBottom: '3px solid transparent',
    },
    '&:focus-visible': {
        outline: `solid ${theme.colors.aodaFocused} 6px`,
    },
    // for activeClassName
    '&.active': {
        borderBottom: `3px solid ${theme.colors.primary}`,
    },
});
const StyledExternalLink = styled(Link)(LinkStyles);
const StyledLink = styled(NavLink)(LinkStyles);
export const ModelBrandName = styled(Copy)(ModelBrandNameStyles);
const Desktop = ({ specialTrimPage, options, selectedOption, modelBadge, navItems, bapBaseUrl, bapLabel, bapUrlParams, offersBaseUrl, offersUrlParams, modelName, bodyStyle, isDark, buildButtonStyling, showCTA, variant, vehicleType, selectedTrim, rendering, gtm, showModelNameBadge, }) => {
    var _a, _b, _c, _d, _e;
    const { t } = useTranslation();
    const { queryParam } = useOffersQueryParam(offersUrlParams);
    const gtmOverviewLabel = (_a = t('Pages.Models.Exploration.overviewNavLabel', { lng: 'en' })) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    const gtmOffersLabel = (_b = t('Pages.Models.Exploration.offersNavLabel', { lng: 'en' })) === null || _b === void 0 ? void 0 : _b.toLowerCase();
    const appName = useAppName();
    const backgroundColor = isDark ? 'black' : 'white';
    const textColor = isDark ? 'white' : 'black';
    const gtmModelName = gtm === null || gtm === void 0 ? void 0 : gtm.gtmModelName;
    const [isOpen, setIsOpen] = useState(false);
    const selectedName = ((_e = (_d = (_c = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.modelNav) === null || _c === void 0 ? void 0 : _c.overviewPage) === null || _d === void 0 ? void 0 : _d.sectionNavLabel) === null || _e === void 0 ? void 0 : _e.value) || (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.name);
    const dropdownRef = useRef(null);
    const linkColor = isDark ? 'white' : 'black';
    useEffect(() => {
        const handleEvent = (event) => {
            var _a;
            const shouldHandle = (dropdownRef === null || dropdownRef === void 0 ? void 0 : dropdownRef.current) && !((_a = dropdownRef === null || dropdownRef === void 0 ? void 0 : dropdownRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target));
            if (shouldHandle)
                setIsOpen(false);
        };
        document.addEventListener('click', handleEvent);
        return () => {
            document.removeEventListener('click', handleEvent);
        };
    }, []);
    const renderBadge = (modelBadge === null || modelBadge === void 0 ? void 0 : modelBadge.src) ? (React.createElement(ModelBadge, { "data-testid": "cy-model-nav-name-badge", src: modelBadge.src, alt: modelBadge.alt })) : (React.createElement(ModelBrandName, { "data-testid": "cy-model-nav-name-text-badge" }, modelName));
    return (React.createElement(Box, { display: "flex", height: appName === HONDA_SITE_NAME ? '84px' : '75px', alignItems: "center", boxShadow: isDark ? 'darkBox.subNav' : 'box.subNav', backgroundColor: backgroundColor, "data-testid": "cy-model-page-nav-desktop-wrapper" },
        React.createElement(Wrapper, { gutters: [false, false], height: "100%" },
            React.createElement(Row, { height: "100%" },
                React.createElement(Column, { width: (specialTrimPage ? 3 : 4) / 12, display: "flex", alignItems: "center", height: "100%" },
                    React.createElement(Box, { position: "relative", pl: "m", ref: dropdownRef, "data-testid": "cy-model-nav-years-dropdown" },
                        React.createElement(Toggle, { setIsOpen: () => {
                                setIsOpen(!isOpen);
                            }, isOpen: isOpen, label: selectedName && React.createElement(Label, { color: linkColor }, compiler(stripMarkdownHeading(selectedName))), "aria-haspopup": "true", "aria-expanded": isOpen, useToggle: (options === null || options === void 0 ? void 0 : options.length) > 1, backgroundColor: backgroundColor }),
                        React.createElement(Options, { as: "ul", role: "menu", isOpen: isOpen, numOptions: options === null || options === void 0 ? void 0 : options.length, boxShadow: "box.subNav", backgroundColor: isDark ? backgroundColor : 'grey.5', overflow: "hidden", position: "absolute", top: "61px", width: "auto", left: 0, border: isOpen ? '1px solid' : 'none', borderTop: "none", borderColor: "grey.1" }, options === null || options === void 0 ? void 0 : options.map((option) => {
                            var _a, _b, _c, _d;
                            // PE dropdown options are retireved from modelNav?.overviewPage?.sectionNavLabel?.value
                            const menuLink = ((_c = (_b = (_a = option === null || option === void 0 ? void 0 : option.modelNav) === null || _a === void 0 ? void 0 : _a.overviewPage) === null || _b === void 0 ? void 0 : _b.sectionNavLabel) === null || _c === void 0 ? void 0 : _c.value) || option.name;
                            return (React.createElement(Box, { as: "li", pt: "16px", key: option.url, px: "m", display: "flex", alignItems: "center", role: "menuitem", "aria-label": menuLink },
                                React.createElement(OptionLink, { "data-testid": "cy-model-nav-year-link", color: linkColor, ...(option.legacyPageUrl
                                        ? { as: 'a', href: option.legacyPageUrl }
                                        : { as: NavLink, to: (_d = (option.correspondingUrl || option.url)) !== null && _d !== void 0 ? _d : '' }), disableHover: true, onClick: () => {
                                        gtmSelectYear(option === null || option === void 0 ? void 0 : option.name);
                                        setIsOpen(false);
                                    }, selected: selectedName && menuLink.toLowerCase() === (selectedName === null || selectedName === void 0 ? void 0 : selectedName.toLowerCase()), isOpen: isOpen }, compiler(stripMarkdownHeading(menuLink)))));
                        }))),
                    React.createElement(Optional, { when: showModelNameBadge }, renderBadge)),
                React.createElement(Column, { as: "ul", width: (specialTrimPage ? 9 : 8) / 12, display: "flex", alignItems: "center", justifyContent: "space-around" },
                    selectedOption && (React.createElement(NavItem, null,
                        React.createElement(StyledLink, { color: textColor, to: selectedOption.url, activeClassName: "active", "data-gtm-model": gtmModelName, "data-gtm-body-style": bodyStyle, "data-gtm-interaction-type": "primary model nav", "data-gtm-title": gtmOverviewLabel, exact: true }, t('Pages.Models.Exploration.overviewNavLabel')))), navItems === null || navItems === void 0 ? void 0 :
                    navItems.map((navItem) => {
                        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
                        return (React.createElement(NavItem, { key: navItem.url },
                            React.createElement(StyledLink, { "data-testid": "cy-model-nav-item", color: textColor, to: navItem.url, activeClassName: "active", "data-gtm-model": gtmModelName, "data-gtm-body-style": bodyStyle, "data-gtm-interaction-type": "primary model nav", "data-gtm-title": (_a = navItem.name) === null || _a === void 0 ? void 0 : _a.toLowerCase(), "aria-label": ((_d = (_c = (_b = navItem.nameBadge) === null || _b === void 0 ? void 0 : _b.item) === null || _c === void 0 ? void 0 : _c.value) === null || _d === void 0 ? void 0 : _d.src)
                                    ? (_f = (_e = navItem.nameBadge) === null || _e === void 0 ? void 0 : _e.item) === null || _f === void 0 ? void 0 : _f.value
                                    : ((_g = navItem.sectionNavLabel) === null || _g === void 0 ? void 0 : _g.value) || navItem.name }, ((_k = (_j = (_h = navItem.nameBadge) === null || _h === void 0 ? void 0 : _h.item) === null || _j === void 0 ? void 0 : _j.value) === null || _k === void 0 ? void 0 : _k.src) ? (React.createElement(TrimBadge, { ...(_m = (_l = navItem.nameBadge) === null || _l === void 0 ? void 0 : _l.item) === null || _m === void 0 ? void 0 : _m.value })) : ((_o = navItem.sectionNavLabel) === null || _o === void 0 ? void 0 : _o.value))));
                    }),
                    React.createElement(NavItem, null,
                        React.createElement(StyledExternalLink, { "data-testid": "cy-model-nav-offers", color: textColor, as: "a", href: `${offersBaseUrl}${queryParam}`, "data-gtm-model": gtmModelName, "data-gtm-body-style": bodyStyle, "data-gtm-interaction-type": "primary model nav", "data-gtm-title": gtmOffersLabel, isLinkType: true }, t('Pages.Models.Exploration.offersNavLabel'))),
                    React.createElement(NavItem, { "data-testid": "cy-model-nav-price-and-finance" }, showCTA ? (React.createElement(Button, { as: "a", href: getBapUrlParams(variant, bapBaseUrl, bapUrlParams, t('Shared.Common.priceAndFinanceUrl'), vehicleType, modelName, selectedTrim), styling: buildButtonStyling, "data-gtm-model": gtmModelName, "data-gtm-body-style": bodyStyle, "data-gtm-interaction-type": "cta: build", "data-gtm-component-type": rendering === null || rendering === void 0 ? void 0 : rendering.componentName, "data-gtm-title": "build & price", "aria-label": bapLabel }, bapLabel)) : null))))));
};
export default Desktop;
